import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Home,
  Package,
  Users,
  FileText,
  ShoppingCart,
  Plus,
  Search,
  Settings,
  HelpCircle,
  Clock,
} from 'lucide-react';

interface CommandEntry {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  shortcut?: string[];
  keywords?: string[];
  group: 'navigation' | 'actions' | 'suppliers' | 'products' | 'recent';
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Load recent items from localStorage
  const [recentItems, setRecentItems] = useState<CommandEntry[]>(() => {
    const stored = localStorage.getItem('holescale-command-recent');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });

  const addToRecent = useCallback((item: CommandEntry) => {
    setRecentItems((prev) => {
      const filtered = prev.filter((i) => i.id !== item.id);
      const updated = [{ ...item, group: 'recent' as const }, ...filtered].slice(0, 5);
      localStorage.setItem('holescale-command-recent', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Define all available commands (static nav/actions - recent appended separately)
  const staticCommands: CommandEntry[] = useMemo(
    () => [
      {
        id: 'nav-dashboard',
        label: 'Dashboard',
        icon: Home,
        action: () => navigate('/buyer/dashboard'),
        shortcut: ['G', 'D'],
        keywords: ['home', 'overview'],
        group: 'navigation',
      },
      {
        id: 'nav-inventory',
        label: 'Inventory',
        icon: Package,
        action: () => navigate('/buyer/inventory'),
        shortcut: ['G', 'I'],
        keywords: ['stock', 'items', 'products'],
        group: 'navigation',
      },
      {
        id: 'nav-suppliers',
        label: 'My Suppliers',
        icon: Users,
        action: () => navigate('/buyer/suppliers'),
        shortcut: ['G', 'S'],
        keywords: ['vendors', 'partners'],
        group: 'navigation',
      },
      {
        id: 'nav-quotes',
        label: 'Quotes',
        icon: FileText,
        action: () => navigate('/buyer/quotes'),
        shortcut: ['G', 'Q'],
        keywords: ['rfq', 'pricing'],
        group: 'navigation',
      },
      {
        id: 'nav-orders',
        label: 'Orders',
        icon: ShoppingCart,
        action: () => navigate('/buyer/orders'),
        shortcut: ['G', 'O'],
        keywords: ['purchases', 'history'],
        group: 'navigation',
      },
      {
        id: 'nav-find-suppliers',
        label: 'Find Suppliers',
        icon: Search,
        action: () => navigate('/buyer/suppliers/find'),
        keywords: ['discover', 'search', 'browse'],
        group: 'navigation',
      },
      {
        id: 'action-create-rfq',
        label: 'Create RFQ',
        description: 'Request quotes from suppliers',
        icon: Plus,
        action: () => navigate('/buyer/rfq/new'),
        shortcut: ['C'],
        keywords: ['new', 'request', 'quote'],
        group: 'actions',
      },
      {
        id: 'action-add-inventory',
        label: 'Add Inventory Item',
        description: 'Track a new product',
        icon: Package,
        action: () => {
          navigate('/buyer/inventory');
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('open-add-inventory'));
          }, 100);
        },
        shortcut: ['N'],
        keywords: ['new', 'product', 'stock'],
        group: 'actions',
      },
      {
        id: 'action-settings',
        label: 'Settings',
        description: 'Manage your account',
        icon: Settings,
        action: () => navigate('/buyer/settings'),
        keywords: ['preferences', 'profile', 'account'],
        group: 'actions',
      },
      {
        id: 'action-help',
        label: 'Help & Support',
        description: 'Get help or contact support',
        icon: HelpCircle,
        action: () => window.open('https://help.holescale.com', '_blank'),
        keywords: ['support', 'contact', 'faq'],
        group: 'actions',
      },
    ],
    [navigate]
  );

  const allCommands = useMemo(
    () => [...recentItems, ...staticCommands.filter((c) => !recentItems.some((r) => r.id === c.id))],
    [recentItems, staticCommands]
  );

  // Group commands (cmdk handles filtering by CommandItem value)
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandEntry[]> = {
      recent: [],
      navigation: [],
      actions: [],
      suppliers: [],
      products: [],
    };

    allCommands.forEach((cmd) => {
      const g = cmd.group;
      if (groups[g] && !groups[g].some((x) => x.id === cmd.id)) {
        groups[g].push(cmd);
      }
    });

    return groups;
  }, [allCommands]);

  const handleSelect = (command: CommandEntry) => {
    addToRecent(command);
    command.action();
    setOpen(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {groupedCommands.recent.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {groupedCommands.recent.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${cmd.description || ''} ${(cmd.keywords || []).join(' ')}`}
                  onSelect={() => handleSelect(cmd)}
                  className="flex items-center gap-4"
                >
                  <Clock className="size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{cmd.label}</div>
                    {cmd.description && (
                      <div className="truncate text-xs text-muted-foreground">{cmd.description}</div>
                    )}
                  </div>
                  {cmd.shortcut && cmd.shortcut.length > 0 && (
                    <div className="flex shrink-0 items-center gap-1">
                      {cmd.shortcut.map((key) => (
                        <kbd
                          key={key}
                          className="rounded bg-muted px-2 py-1 font-mono text-xs"
                        >
                          {key}
                        </kbd>
                      ))}
                    </div>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {groupedCommands.navigation.length > 0 && (
          <>
            <CommandGroup heading="Navigation">
              {groupedCommands.navigation.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${cmd.description || ''} ${(cmd.keywords || []).join(' ')}`}
                  onSelect={() => handleSelect(cmd)}
                  className="flex items-center gap-4"
                >
                  <cmd.icon className="size-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{cmd.label}</span>
                  {cmd.shortcut && cmd.shortcut.length > 0 && (
                    <div className="flex shrink-0 items-center gap-1">
                      {cmd.shortcut.map((key) => (
                        <kbd
                          key={key}
                          className="rounded bg-muted px-2 py-1 font-mono text-xs"
                        >
                          {key}
                        </kbd>
                      ))}
                    </div>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {groupedCommands.actions.length > 0 && (
          <CommandGroup heading="Actions">
            {groupedCommands.actions.map((cmd) => (
              <CommandItem
                key={cmd.id}
                value={`${cmd.label} ${cmd.description || ''} ${(cmd.keywords || []).join(' ')}`}
                onSelect={() => handleSelect(cmd)}
                className="flex items-center gap-4"
              >
                <cmd.icon className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{cmd.label}</div>
                  {cmd.description && (
                    <div className="truncate text-xs text-muted-foreground">{cmd.description}</div>
                  )}
                </div>
                {cmd.shortcut && cmd.shortcut.length > 0 && (
                  <div className="flex shrink-0 items-center gap-1">
                    {cmd.shortcut.map((key) => (
                      <kbd
                        key={key}
                        className="rounded bg-muted px-2 py-1 font-mono text-xs"
                      >
                        {key}
                      </kbd>
                    ))}
                  </div>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>

      <div className="border-t p-2 text-center text-xs text-muted-foreground">
        Use{' '}
        <kbd className="rounded bg-muted px-1.5 py-0.5 font-mono">↑</kbd>
        <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono">↓</kbd> to navigate,{' '}
        <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono">↵</kbd> to select,{' '}
        <kbd className="ml-1 rounded bg-muted px-1.5 py-0.5 font-mono">Esc</kbd> to close
      </div>
    </CommandDialog>
  );
}
